/**
 * importedDrafts.js — Servicio para vincular borradores importados (CSV) a
 * usuarios reales de Firebase y escribir su historial de mediciones.
 *
 * Firestore:
 *  - importedDrafts/{draftId}  → registro del vínculo (quién, cuándo)
 *  - users/{uid}/measurements/{autoId} → mediciones históricas escritas
 *
 * GCP / gateway sync → TODO: diferido hasta que el endpoint salga de mantenimiento
 */

import { db } from "@/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  writeBatch,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";

// ── Helpers ────────────────────────────────────────────────────────────────

/**
 * Convierte "DD/MM/YYYY" → Firestore Timestamp.
 * Si la fecha es inválida devuelve el timestamp actual.
 */
function dateStringToTimestamp(dateStr) {
  if (!dateStr) return Timestamp.now();
  const [d, m, y] = dateStr.split("/");
  const parsed = new Date(`${y}-${m}-${d}T12:00:00Z`);
  if (isNaN(parsed)) return Timestamp.now();
  return Timestamp.fromDate(parsed);
}

/**
 * Filtra campos nulos/undefined de un objeto de medición y
 * remapea "fc" → "heartRate" para coincidir con el schema de measurements.
 */
function cleanFields(csvMeasurement) {
  const MAP = {
    peso: "peso",
    talla: "talla",
    imc: "imc",
    cintura: "cintura",
    paSistolica: "paSistolica",
    paDiastolica: "paDiastolica",
    glucemia: "glucemia",
    oximetria: "oximetria",
    fc: "heartRate",
  };
  const out = {};
  for (const [csvKey, appKey] of Object.entries(MAP)) {
    const v = csvMeasurement[csvKey];
    if (v !== null && v !== undefined) out[appKey] = v;
  }
  return out;
}

// ── Leer estado de vínculos ─────────────────────────────────────────────────

/**
 * Carga el mapa { draftId → linkDoc } desde la colección importedDrafts en
 * Firestore. Devuelve un objeto vacío si la colección no existe aún.
 */
export async function loadLinkedStatus() {
  const snap = await getDocs(collection(db, "importedDrafts"));
  const map = {};
  snap.forEach((d) => {
    map[d.id] = d.data();
  });
  return map;
}

// ── Vincular ────────────────────────────────────────────────────────────────

/**
 * Vincula un borrador importado a un usuario real de Firebase:
 *  1. Escribe cada medición CSV al subcollection users/{uid}/measurements/
 *  2. Guarda el registro del vínculo en importedDrafts/{draftId}
 *
 * @param {string} draftId    - ID del borrador (ej. "draft-001")
 * @param {string} draftTarjeta - Tarjeta WE360 del borrador
 * @param {string} uid        - Firebase UID del usuario real
 * @param {{ email: string, displayName?: string }} userInfo
 * @param {Array}  measurements - Array de mediciones del borrador
 * @returns {Promise<{ measurementsWritten: number }>}
 */
export async function linkDraftToUser(draftId, draftTarjeta, uid, userInfo, measurements) {
  const batch = writeBatch(db);
  let count = 0;

  // 1. Escribir mediciones al subcollection del usuario
  for (const m of measurements) {
    const fields = cleanFields(m);
    if (Object.keys(fields).length === 0) continue; // sin datos útiles, saltar

    const measureRef = doc(collection(db, "users", uid, "measurements"));
    batch.set(measureRef, {
      method: "imported",
      source: "csv-import",
      fields,
      note: `Evaluación importada: ${m.source || "CSV"}${m.condicionTexto ? ` — ${m.condicionTexto}` : ""}`,
      createdAt: dateStringToTimestamp(m.date),
      importedAt: serverTimestamp(),
      importTarjeta: draftTarjeta || null,
    });
    count++;
  }

  // 2. Registrar el vínculo en importedDrafts/{draftId}
  const linkRef = doc(db, "importedDrafts", draftId);
  batch.set(linkRef, {
    draftId,
    tarjeta: draftTarjeta || null,
    linkedUid: uid,
    linkedEmail: userInfo.email || null,
    linkedName: userInfo.displayName || null,
    measurementsWritten: count,
    linkedAt: serverTimestamp(),
    status: "linked",
    // TODO: gcpSynced: false → pendiente cuando el gateway salga de mantenimiento
  });

  await batch.commit();
  return { measurementsWritten: count };
}

/**
 * Elimina el vínculo de un borrador (sin borrar las mediciones ya escritas).
 * Útil para corregir un match incorrecto.
 */
export async function unlinkDraft(draftId) {
  const linkRef = doc(db, "importedDrafts", draftId);
  const existing = await getDoc(linkRef);
  if (!existing.exists()) return;

  await setDoc(linkRef, {
    ...existing.data(),
    status: "unlinked",
    unlinkedAt: serverTimestamp(),
    linkedUid: null,
  });
}
